import { useEffect, useState } from "react";

import { useKeystone } from "data/ss-data-hooks";
import { UnauthorizedRequestError, isError } from "data/ss-error";
import { useEnrollmentStatusDataQuery } from "gql/__generated__/hooks";
import { type Policy } from "gql/types";
import { handleUnauthorized, logException, trackEvent, useFlags } from "utils";
import {
  type KeystoneOfferEligibility,
  type OfferStatusCode,
} from "utils/keystone-api-mapping";

const ENROLLED_STATUSES: OfferStatusCode[] = ["ENROLLED", "ORDERED"];

export const useEligibleOffers = (policy: Policy) => {
  const keystone = useKeystone();
  const { shingleResealOffer, tingOffer } = useFlags();
  const [rawOffers, setRawOffers] = useState<KeystoneOfferEligibility[]>([]);
  const [allOffers, setAllOffers] = useState<KeystoneOfferEligibility[]>([]);
  const [enrolledOffers, setEnrolledOffers] = useState<
    KeystoneOfferEligibility[]
  >([]);
  const [unenrolledOffers, setUnenrolledOffers] = useState<
    KeystoneOfferEligibility[]
  >([]);
  const [loading, setLoading] = useState(false);

  const { loading: enrollmentLoading, data: enrollmentStatusData } =
    useEnrollmentStatusDataQuery({
      skip: !policy.policyId,
      variables: { policyID: policy.policyId ?? "" },
    });
  const { isEnrolledInLeakBot } = enrollmentStatusData?.userDetails ?? {};

  // Go get the offers
  useEffect(() => {
    setRawOffers([]);
    if (!policy?.insightPolicyId) {
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await keystone.getPolicyOffers(
          policy?.insightPolicyId ?? ""
        );
        if (!isError(res)) {
          const offers = res;
          setRawOffers(offers);
          trackEvent("Eligible Offers Loaded", {
            offers: offers.map((offer) => offer.offeringInternalName),
            keystonePolicyId: policy.insightPolicyId,
          });
        } else {
          if (res instanceof UnauthorizedRequestError) {
            handleUnauthorized({
              endpoint: `offers/${policy?.insightPolicyId}`,
            });
          }
        }
      } catch (err) {
        // swallow the error, we just won't show any offers
        logException(err);
      }
      setLoading(false);
    })();
  }, [keystone, policy]);

  // Filter the eligible offers
  useEffect(() => {
    const implementedOfferingNames: string[] = ["leakbot"];
    if (tingOffer) {
      implementedOfferingNames.push("ting-offer");
    }
    if (shingleResealOffer) {
      implementedOfferingNames.push("wind-defense");
    }

    const offers = [...rawOffers];
    if (isEnrolledInLeakBot) {
      offers.push({
        isFeatured: false,
        insightPolicyId: policy.insightPolicyId,
        isEligible: true,
        offerId: null,
        offerStatusCode: "ENROLLED",
        offerUrl: "",
        offeringId: "",
        offeringInternalName: "leakbot",
        offeringMarketingName: "LeakBot",
        additionalDetails: [],
      });
    }

    setAllOffers(
      offers.filter(
        (offer) =>
          offer.isEligible &&
          implementedOfferingNames.includes(offer.offeringInternalName)
      )
    );
    setEnrolledOffers(
      offers.filter(
        (offer) =>
          offer?.offerStatusCode &&
          ENROLLED_STATUSES.includes(offer?.offerStatusCode) &&
          implementedOfferingNames.includes(offer.offeringInternalName)
      )
    );
    setUnenrolledOffers(
      offers.filter(
        (offer) =>
          offer.isEligible &&
          offer?.offerStatusCode &&
          !ENROLLED_STATUSES.includes(offer?.offerStatusCode) &&
          implementedOfferingNames.includes(offer.offeringInternalName)
      )
    );
  }, [
    isEnrolledInLeakBot,
    policy.insightPolicyId,
    rawOffers,
    shingleResealOffer,
    tingOffer,
  ]);

  return {
    eligibleOffers: {
      allOffers,
      enrolledOffers,
      unenrolledOffers,
    },
    loading: loading || enrollmentLoading,
  };
};
